import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getOrganizationDetail,
  OrganizationAdminDetail,
  OrganizationAdminUser,
  setUserHiddenFromMembers,
  setUserRole,
  revokeInvitation,
  resendInvitation,
  OrganizationAdminInvitation,
} from "../../services/AdminOrganizations";
import { Switch } from "@/components/ui/switch";
import PlanBadge from "./PlanBadge";
import PlanEditor from "./PlanEditor";
import WorkspacePanel from "./WorkspacePanel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { formatSek, formatUsdTitle } from "./usage/sek";
import {
  ArrowLeft,
  EyeOff,
  Loader2,
  RefreshCw,
  Search,
  Send,
  X,
} from "lucide-react";

const ACTIVE_WINDOW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

const formatDate = (iso: string | null): string => {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

/** "2 hours ago", "yesterday", "3 weeks ago" */
const formatRelative = (iso: string | null): string => {
  if (!iso) return "Never";
  const seconds = (new Date(iso).getTime() - Date.now()) / 1000;
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 365 * 24 * 3600],
    ["month", 30 * 24 * 3600],
    ["week", 7 * 24 * 3600],
    ["day", 24 * 3600],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size)
      return relative.format(Math.round(seconds / size), unit);
  }
  return "Just now";
};

const formatCostSek = (n: number): string => (n === 0 ? "-" : n < 0.01 ? "< 0.01 SEK" : formatSek(n));

const formatTokens = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
};

const fullName = (u: OrganizationAdminUser): string =>
  [u.firstName, u.lastName].filter(Boolean).join(" ");

const isRecentlyActive = (u: OrganizationAdminUser): boolean =>
  u.lastActiveAt != null &&
  Date.now() - new Date(u.lastActiveAt).getTime() < ACTIVE_WINDOW_DAYS * DAY_MS;

const Stat: React.FC<{ label: string; value: string; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <Card className="p-4">
    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
    <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
  </Card>
);

const OrganizationDetail: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [detail, setDetail] = useState<OrganizationAdminDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [search, setSearch] = useState("");
  const [savingHidden, setSavingHidden] = useState<string | null>(null);
  const [invitationBusy, setInvitationBusy] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!organizationId) return;
    const res = await getOrganizationDetail(organizationId);
    if (res.success && res.data) {
      setDetail(res.data);
      setNotFound(false);
    } else if (res.status === 404 || res.status === 400) {
      setNotFound(true);
    } else {
      toast.error("Failed to load organization");
    }
  }, [organizationId]);

  useEffect(() => {
    setLoading(true);
    fetchDetail().finally(() => setLoading(false));
  }, [fetchDetail]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await fetchDetail();
    setRefreshing(false);
  }, [fetchDetail]);

  const toggleHidden = useCallback(
    async (user: OrganizationAdminUser, hidden: boolean) => {
      if (!organizationId) return;
      setSavingHidden(user.id);
      try {
        const res = await setUserHiddenFromMembers(
          organizationId,
          user.id,
          hidden,
        );
        if (res.success) {
          setDetail((d) =>
            d
              ? {
                  ...d,
                  users: d.users.map((u) =>
                    u.id === user.id ? { ...u, hiddenFromMembers: hidden } : u,
                  ),
                }
              : d,
          );
          toast.success(
            hidden
              ? `${user.email} is now hidden from other members`
              : `${user.email} is visible to other members again`,
          );
        } else {
          toast.error("Failed to update user");
        }
      } finally {
        setSavingHidden(null);
      }
    },
    [organizationId],
  );

  const toggleAdmin = useCallback(
    async (user: OrganizationAdminUser, admin: boolean) => {
      if (!organizationId) return;
      setSavingHidden(user.id);
      try {
        const res = await setUserRole(organizationId, user.id, admin ? "admin" : "member");
        if (res.success) {
          setDetail((d) =>
            d
              ? {
                  ...d,
                  users: d.users.map((u) =>
                    u.id === user.id ? { ...u, role: admin ? "admin" : "member" } : u,
                  ),
                }
              : d,
          );
          toast.success(admin ? `${user.email} is now an admin` : `${user.email} is now a member`);
        } else {
          toast.error("Failed to update user");
        }
      } finally {
        setSavingHidden(null);
      }
    },
    [organizationId],
  );

  const handleInvitation = useCallback(
    async (
      invitation: OrganizationAdminInvitation,
      action: "revoke" | "resend",
    ) => {
      setInvitationBusy(invitation.id);
      try {
        const res =
          action === "revoke"
            ? await revokeInvitation(invitation.id)
            : await resendInvitation(invitation.id);
        if (res.success) {
          toast.success(
            action === "revoke"
              ? "Invitation revoked"
              : `Invitation sent again to ${invitation.email}`,
          );
          await fetchDetail();
        } else {
          toast.error(
            res.errors?.[0]?.detail ||
              res.errors?.[0]?.title ||
              `Failed to ${action} the invitation`,
          );
        }
      } finally {
        setInvitationBusy(null);
      }
    },
    [fetchDetail],
  );

  const users = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = (detail?.users ?? []).filter(
      (u) =>
        !q ||
        u.email.toLowerCase().includes(q) ||
        fullName(u).toLowerCase().includes(q),
    );
    // Most recently active first; people who never used Queri last
    return [...rows].sort((a, b) => {
      if (a.lastActiveAt && b.lastActiveAt)
        return b.lastActiveAt.localeCompare(a.lastActiveAt);
      if (a.lastActiveAt || b.lastActiveAt) return a.lastActiveAt ? -1 : 1;
      return a.email.localeCompare(b.email);
    });
  }, [detail, search]);

  const stats = useMemo(() => {
    const all = detail?.users ?? [];
    return {
      active: all.filter(isRecentlyActive).length,
      invited: all.filter((u) => u.status !== "ACTIVE").length,
      cost30d: all.reduce((sum, u) => sum + u.costSek30d, 0),
    };
  }, [detail]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (notFound || !detail) {
    return (
      <div className="space-y-4">
        <BackLink />
        <Card className="p-8 text-center text-sm text-muted-foreground">
          {notFound
            ? "This organization does not exist or has been deleted."
            : "Could not load this organization."}
        </Card>
      </div>
    );
  }

  const org = detail.organization;

  return (
    <div className="space-y-6">
      <BackLink />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              {org.name}
            </h1>
            <PlanBadge plan={org.plan} planEndsAt={org.planEndsAt} />
            {org.isWorkspace && org.slug && (
              <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                {org.slug}.queri.law
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {org.planEndsAt ? `Plan ends ${formatDate(org.planEndsAt)}` : 'Open-ended plan'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={refreshing}
        >
          <RefreshCw
            className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>

      <PlanEditor org={org} onSaved={() => void fetchDetail()} />

      <WorkspacePanel
        organizationId={org.id}
        organizationName={org.displayName || org.name}
        onChanged={() => void fetchDetail()}
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat
          label="Users"
          value={String(detail.users.length)}
          hint={
            stats.invited ? `${stats.invited} not signed up yet` : undefined
          }
        />
        <Stat
          label={`Active (${ACTIVE_WINDOW_DAYS}d)`}
          value={String(stats.active)}
          hint={
            detail.users.length
              ? `${Math.round((stats.active / detail.users.length) * 100)}% of users`
              : undefined
          }
        />
        <Stat
          label={`AI cost (${ACTIVE_WINDOW_DAYS}d)`}
          value={stats.cost30d ? formatSek(stats.cost30d) : formatSek(0)}
        />
        <Stat
          label="Tokens"
          value={formatTokens(org.tokensConsumed)}
          hint={
            org.tokenLimit != null
              ? `of ${formatTokens(org.tokenLimit)} limit`
              : undefined
          }
        />
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
          <h2 className="text-base font-semibold">Users</h2>
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Last active</TableHead>
              <TableHead className="text-right">Chats</TableHead>
              <TableHead className="text-right">
                AI cost ({ACTIVE_WINDOW_DAYS}d)
              </TableHead>
              <TableHead className="text-right" title="Admins manage the organization's members">
                Admin
              </TableHead>
              <TableHead
                className="text-right"
                title="Hidden users don't appear in member lists other members see"
              >
                Hidden
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  {detail.users.length === 0
                    ? "This organization has no users yet."
                    : "No users match your search."}
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <div className="flex items-center gap-1.5 font-medium">
                      {fullName(u) || u.email}
                      {u.hiddenFromMembers && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600"
                          title="Hidden from other members of this organization"
                        >
                          <EyeOff className="h-3 w-3" />
                          Hidden
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {fullName(u) ? u.email : null}
                      {fullName(u) && u.jobTitle ? " · " : null}
                      {u.jobTitle}
                    </div>
                  </TableCell>
                  <TableCell>
                    {u.status === "ACTIVE" ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                        Active
                      </span>
                    ) : (
                      <span
                        className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
                        title="Invited, has not set a password yet"
                      >
                        Invited
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(u.createdAt)}
                  </TableCell>
                  <TableCell
                    className={`text-sm ${u.lastActiveAt ? "" : "text-muted-foreground"}`}
                    title={
                      u.lastActiveAt
                        ? new Date(u.lastActiveAt).toLocaleString()
                        : undefined
                    }
                  >
                    {formatRelative(u.lastActiveAt)}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {u.chatCount || "-"}
                  </TableCell>
                  <TableCell
                    className="text-right text-sm tabular-nums"
                    title={formatUsdTitle(u.costUsd30d)}
                  >
                    {formatCostSek(u.costSek30d)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Switch
                      checked={u.role === "admin"}
                      disabled={savingHidden === u.id}
                      onCheckedChange={(v) => void toggleAdmin(u, v)}
                      aria-label={`Make ${u.email} an admin`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Switch
                      checked={u.hiddenFromMembers}
                      disabled={savingHidden === u.id}
                      onCheckedChange={(v) => void toggleHidden(u, v)}
                      aria-label={`Hide ${u.email} from other members`}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {detail.pendingInvitations.length > 0 && (
        <Card className="overflow-hidden">
          <div className="border-b p-4">
            <h2 className="text-base font-semibold">Pending invitations</h2>
            <p className="text-sm text-muted-foreground">
              Invitation links that have not been used yet.
            </p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sent to</TableHead>
                <TableHead>Invited by</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.pendingInvitations.map((i) => (
                <TableRow key={i.id}>
                  <TableCell className="text-sm">
                    {i.email ?? (
                      <span className="text-muted-foreground">Shared link</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {i.invitedBy ?? "-"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(i.createdAt)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {i.expired ? (
                      <span className="text-muted-foreground">
                        Expired {formatDate(i.expiresAt)}
                      </span>
                    ) : (
                      formatDate(i.expiresAt)
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {i.email && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={invitationBusy === i.id}
                          onClick={() => void handleInvitation(i, "resend")}
                          title="Send the invitation again, valid for 7 more days"
                        >
                          <Send className="h-3 w-3" />
                          Resend
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={invitationBusy === i.id}
                        onClick={() => void handleInvitation(i, "revoke")}
                        title="The link stops working"
                      >
                        <X className="h-3 w-3" />
                        Revoke
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

const BackLink: React.FC = () => (
  <Link
    to="/admin/organizations"
    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
  >
    <ArrowLeft className="h-4 w-4" />
    Organizations
  </Link>
);

export default OrganizationDetail;
